import React, { useState, useEffect } from 'react';
import { GripVertical } from 'lucide-react';
import { Badge } from "@/components/ui/badge";

const beforeText = `In today's **ever-evolving digital landscape**, we understand that businesses are striving to **unlock their true potential** as they navigate through this **noisy digital world**.\n\nLook no further, as we've got you covered with a **comprehensive guide** to ensure **optimal results** in this **dynamic ecosystem**. It's essential to note that **leveraging** the right **strategies** is **vital** for success.\n\nFurthermore, we know that transitioning to a more **holistic approach** can be daunting. Therefore, we're eager to **unveil** our **revolutionary framework** that will help you navigate this journey with **synergy** and **innovation**.\n\nIn conclusion, by embracing this **comprehensive approach**, you'll be well-equipped to **unleash your potential** in the **evolving landscape** of **digital transformation**.`;

const afterText = `The brutal truth about content marketing is that most people are doing it wrong, dead wrong. They're wasting time trying to sound smart instead of making sales.

What actually works is that you write like you're explaining it to a friend over coffee. No corporate BS. No "thought leadership." Just real talk that makes people think "finally, someone who gets it."

The reason this works is because of simple psychology: People don't buy from robots. They buy from humans they trust. And trust comes from being real, not from sounding like you swallowed a business textbook.

So stop trying to impress twitter nerds and tiktok bros and LinkedIn and just start writing content that makes your readers feel seen, understood, and ready to throw money at you to solve their problems.`;

function TextComparison() {
  const [inset, setInset] = useState<number>(50);
  const [onMouseDown, setOnMouseDown] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const onMouseMove = (e: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent) => {
    if (!onMouseDown || !isDragging) return;

    const container = document.querySelector('.slider-container');
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    let x = 0;

    if ("touches" in e && e.touches.length > 0) {
      x = e.touches[0].clientX - rect.left;
    } else if ("clientX" in e) {
      x = e.clientX - rect.left;
    }
    
    // Constrain x to container bounds
    x = Math.max(0, Math.min(x, rect.width));
    const percentage = Math.max(0, Math.min((x / rect.width) * 100, 100));
    setInset(percentage);
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      onMouseMove(e);
    };

    const handleGlobalMouseUp = () => {
      handleDragEnd();
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.body.style.cursor = 'ew-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.body.style.cursor = 'default';
    };
  }, [isDragging]);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setOnMouseDown(true);
    setIsDragging(true);
    onMouseMove(e);
  };

  const handleDragEnd = () => {
    setOnMouseDown(false);
    setIsDragging(false);
    document.body.style.cursor = 'default';
  };

  return (
    <section className="py-24 bg-cream">
      <div className="container mx-auto px-2 md:px-4">
        <div className="flex flex-col gap-4 relative">
          <div>
            <Badge variant="secondary" className="bg-black text-white hover:bg-black/80">Before & After</Badge>
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl tracking-tighter font-bold">
              See the difference
            </h2>
            <p className="text-lg leading-relaxed tracking-tight text-muted-foreground mt-2">
              Drag the slider to compare AI-generated corporate nonsense with content that actually sounds like a human wrote it.
            </p>
          </div>
          <div className="pt-6 w-full">
            <div 
              className="relative w-full h-[450px] md:h-[400px] rounded-xl shadow-lg border border-gray-100 select-none bg-white slider-container overflow-visible"
              onMouseMove={onMouseMove}
              onMouseLeave={() => isDragging && onMouseMove}
              onMouseUp={handleDragEnd}
              onTouchMove={onMouseMove}
              onTouchEnd={handleDragEnd}
            >
              <div
                className="bg-dogblue w-1 absolute z-20 top-0 -ml-1 select-none h-full overflow-visible"
                style={{
                  left: inset + "%",
                }}
              >
                <button
                  className={`bg-dogblue rounded hover:scale-110 transition-all w-5 h-10 select-none -translate-y-1/2 absolute top-1/2 -ml-2 z-30 cursor-ew-resize flex justify-center items-center overflow-visible ${
                    isDragging ? 'scale-110' : ''
                  }`}
                  onTouchStart={handleDragStart}
                  onMouseDown={handleDragStart}
                  onTouchEnd={() => setOnMouseDown(false)}
                  onMouseUp={() => setOnMouseDown(false)}
                >
                  <GripVertical className="h-4 w-4 text-white select-none" />
                </button>
              </div>
              <div
                className="absolute left-0 top-0 z-10 w-full h-full p-8 text-lg"
                style={{
                  clipPath: "inset(0 0 0 " + inset + "%)",
                }}
              > 
                <div className="relative bg-white rounded-lg p-3 md:p-6 h-full overflow-hidden">
                  <h3 className="font-semibold mb-4 text-dogblue">Not Trash</h3>
                  <div className="h-[calc(100%-2rem)] overflow-y-auto custom-scrollbar whitespace-pre-line">
                    {afterText}
                  </div>
                </div>
              </div>
              <div className="absolute left-0 top-0 w-full h-full p-8 text-lg">
                <div className="relative bg-white rounded-lg p-3 md:p-6 h-full overflow-hidden">
                  <h3 className="font-semibold mb-4 text-poop">Dog S*t</h3>
                  <div className="whitespace-pre-wrap h-[calc(100%-2rem)] overflow-y-auto custom-scrollbar">
                    {beforeText.split('**').map((part, i) => (
                      i % 2 === 0 ? part : <strong key={i} className="font-bold text-poop">{part}</strong>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export { TextComparison };